/**
 * SSML 辞書マスタ画面のエラー / 警告メッセージを日本語に変換するモジュール。
 *
 * ## なぜ必要か
 *
 * バックエンド (`lambda/control-panel-api/index.py`) の入力検証は
 * `raise ValueError("replacement must differ from source_text")` のような**英語**の文を投げ、
 * それが 400 の `message` としてそのまま返る。画面にそのまま出すと
 * 「何が悪いのか」「どの欄を直せばよいのか」が利用者に伝わらない。
 *
 * そこで英語のメッセージを **日本語の文** と **原因の入力欄** に変換する。
 * 原因の欄が特定できるものは、画面側でその欄に (ブラウザ標準の入力検証の吹き出し +
 * 赤枠 + 欄の直下の文で) 表示する。特定できないもの (認証切れ / サーバエラー / 通信断 など) は
 * 画面上部のバナーに出す。
 *
 * ## 保守
 *
 * 対応表 (`SERVER_MESSAGE_RULES`) は `index.py` の `raise ValueError(...)` と対になる。
 * バックエンドに新しい検証文言が増えたときに対応表の更新漏れに気づけるよう、
 * `ssmlDictionaryMessages.test.ts` が `index.py` を読んで突き合わせている。
 */
import { ApiError } from '../api/common';
import type { SsmlDictionaryWarning } from '../api/types';

/** エラーの原因になった入力欄 */
export type SsmlDictionaryErrorField = 'language' | 'source' | 'replacement' | 'note';

/** 画面表示用に整形したエラー */
export interface SsmlDictionaryErrorInfo {
  /** 利用者向けの日本語メッセージ */
  message: string;
  /** 原因の入力欄。特定できない場合は null (バナー表示にする) */
  field: SsmlDictionaryErrorField | null;
}

/** 必須項目が空のときのメッセージ (クライアント側の検証とサーバ文言の対応表で共用する) */
export const REQUIRED_FIELD_MESSAGES = {
  source: '変換元を入力してください',
  replacement: '置換後を入力してください',
} as const;

/** 通信そのものに失敗したとき (fetch が TypeError を投げる) */
export const NETWORK_ERROR_MESSAGE = '通信に失敗しました。ネットワークの接続を確認してください';

/** 原因が分からない例外 */
export const UNEXPECTED_ERROR_MESSAGE = '予期しないエラーが発生しました';

interface ServerMessageRule {
  /** バックエンドの英語メッセージ全体に一致させる正規表現 */
  pattern: RegExp;
  field: SsmlDictionaryErrorField | null;
  message: (match: RegExpMatchArray) => string;
}

/**
 * バックエンドの検証メッセージ → 日本語 + 対象欄の対応表。
 * `index.py` の `raise ValueError(...)` 1 件につき 1 ルール以上を持つ。
 */
export const SERVER_MESSAGE_RULES: readonly ServerMessageRule[] = [
  // ---- 言語 ----
  {
    pattern: /^language_code is required$/,
    field: 'language',
    message: () => '言語を選択してください',
  },
  {
    pattern: /^language_code must be a string$/,
    field: 'language',
    message: () => '言語の指定が正しくありません',
  },
  {
    pattern: /^language_code .+ is not supported/,
    field: 'language',
    message: () =>
      'この言語は辞書に登録できません。日本語・英語・中国語・韓国語から選択してください',
  },

  // ---- 変換元 ----
  {
    pattern: /^source_text is required$/,
    field: 'source',
    message: () => REQUIRED_FIELD_MESSAGES.source,
  },
  {
    pattern: /^source_text must be a string$/,
    field: 'source',
    message: () => '変換元は文字列で入力してください',
  },
  {
    pattern: /^source_text exceeds (\S+) characters$/,
    field: 'source',
    message: (m) => `変換元は${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^source_text must not contain '(.+)'$/,
    field: 'source',
    message: (m) => `変換元に「${m[1]}」は使えません`,
  },

  // ---- 種別 / 有効 (入力欄が特定できないのでバナー表示) ----
  {
    pattern: /^dictionary_type is required$/,
    field: null,
    message: () => '種別を選択してください',
  },
  {
    pattern: /^dictionary_type must be one of: .+$/,
    field: null,
    message: () =>
      '種別が正しくありません。「アクセント＋読み仮名」「読み仮名のみ」「テキスト編集」「SSML構文」のいずれかを選択してください',
  },
  {
    pattern: /^unsupported dictionary_type: .+$/,
    field: null,
    message: () =>
      '種別が正しくありません。「アクセント＋読み仮名」「読み仮名のみ」「テキスト編集」「SSML構文」のいずれかを選択してください',
  },
  {
    pattern: /^enabled must be a boolean$/,
    field: null,
    message: () => '「有効」の指定が正しくありません',
  },

  // ---- 置換後 (共通) ----
  {
    pattern: /^replacement is required$/,
    field: 'replacement',
    message: () => REQUIRED_FIELD_MESSAGES.replacement,
  },
  {
    pattern: /^replacement must be a string$/,
    field: 'replacement',
    message: () => '置換後は文字列で入力してください',
  },
  {
    pattern: /^replacement exceeds (\S+) characters$/,
    field: 'replacement',
    message: (m) => `置換後は${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^replacement must not contain unsupported SSML tags$/,
    field: 'replacement',
    message: () =>
      '置換後に、音声合成(Amazon Polly)で使えないタグが含まれています。<emphasis> や <ruby> などは取り除いてください',
  },
  {
    pattern: /^replacement must not contain '<' or '>'$/,
    field: 'replacement',
    message: () =>
      '置換後に「<」「>」は使えません。タグを使いたいときは種別「SSML構文」を選んでください',
  },
  {
    pattern: /^replacement must differ from source_text$/,
    field: 'replacement',
    message: () => '置換後は変換元と異なる文字列にしてください',
  },

  // ---- 置換後 (読み仮名辞典) ----
  {
    pattern: /^replacement for 'reading' must be hiragana, katakana or 'ー' only$/,
    field: 'replacement',
    message: () => '読み仮名は、ひらがな・カタカナ・長音「ー」だけで入力してください',
  },

  // ---- 置換後 (アクセント辞典) ----
  {
    pattern: /^replacement for 'accent' must not contain '"'$/,
    field: 'replacement',
    message: () => 'アクセント付きの読みに「"」は使えません',
  },
  {
    pattern: /^replacement for 'accent' must be full-width katakana, 'ー' and accent marks \('\)$/,
    field: 'replacement',
    message: () =>
      "アクセント付きの読みは、全角カタカナ・長音「ー」・アクセント記号「'」だけで入力してください",
  },
  {
    pattern: /^accent mark \('\) in replacement must follow a kana$/,
    field: 'replacement',
    message: () => "アクセント記号「'」は先頭に置けません。下がる直前のカナの直後に入れてください",
  },

  // ---- 置換後 (SSML構文) ----
  {
    pattern: /^replacement for 'ssml' must not contain <speak>$/,
    field: 'replacement',
    message: () =>
      '置換後に <speak> は書かないでください。放送文の全体は自動で <speak> に包まれます',
  },
  {
    pattern: /^replacement for 'ssml' must be well-formed SSML$/,
    field: 'replacement',
    message: () =>
      'SSML構文の形が正しくありません。タグの閉じ忘れや入れ子の順序、単独の「&」(「&amp;」と書きます)を確認してください',
  },
  {
    pattern: /^replacement for 'ssml' must not contain pitch attribute$/,
    field: 'replacement',
    message: () =>
      'pitch(音の高さ)は音声合成(Amazon Polly の Neural)で使えないため、指定できません',
  },

  // ---- 補足 ----
  {
    pattern: /^note must be a string$/,
    field: 'note',
    message: () => '補足は文字列で入力してください',
  },
  {
    pattern: /^note exceeds (\S+) characters$/,
    field: 'note',
    message: (m) => `補足は${m[1]}文字以内で入力してください`,
  },

  // ---- リクエスト本文 (通常の画面操作では起きない) ----
  {
    pattern: /^Invalid JSON body: /,
    field: null,
    message: () => '送信内容の形式が正しくありません。画面を再読み込みして、もう一度お試しください',
  },
  {
    pattern: /^Request body must be a JSON object$/,
    field: null,
    message: () => '送信内容の形式が正しくありません。画面を再読み込みして、もう一度お試しください',
  },
];

/**
 * バックエンドの検証メッセージ (英語) を日本語 + 対象欄に変換する。
 * 対応表に無い場合は null。
 */
export function translateServerMessage(message: string): SsmlDictionaryErrorInfo | null {
  for (const rule of SERVER_MESSAGE_RULES) {
    const match = rule.pattern.exec(message);
    if (match) {
      return { message: rule.message(match), field: rule.field };
    }
  }
  return null;
}

/** 日本語 (ひらがな / カタカナ / 漢字) を含むか。含むなら既に利用者向けの文とみなす。 */
function containsJapanese(text: string): boolean {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
}

/** HTTP ステータスとサーバのメッセージから、画面表示用のエラーを作る。 */
function describeHttpError(status: number, serverMessage: string): SsmlDictionaryErrorInfo {
  if (status === 400) {
    const translated = translateServerMessage(serverMessage);
    if (translated) return translated;
    // 対応表に無い 400。日本語ならそのまま、英語なら原文を詳細として添える。
    return {
      message: containsJapanese(serverMessage)
        ? serverMessage
        : `入力内容を確認してください(詳細: ${serverMessage})`,
      field: null,
    };
  }
  if (status === 401 || status === 403) {
    return {
      message: 'ログインの有効期限が切れたか、この操作の権限がありません。再度ログインしてください',
      field: null,
    };
  }
  if (status === 404) {
    return {
      message:
        '対象の辞書が見つかりません。すでに削除された可能性があります。一覧を再読み込みしてください',
      field: null,
    };
  }
  if (status >= 500) {
    return {
      message: 'サーバでエラーが発生しました。時間をおいて、もう一度お試しください',
      field: null,
    };
  }
  return { message: `処理に失敗しました(HTTP ${status})`, field: null };
}

/**
 * API クライアントの例外を、画面表示用の日本語メッセージ + 対象欄に変換する。
 *
 * - `ApiError`: ステータス別に変換 (400 は対応表で欄まで特定する)
 * - `TypeError`: `fetch` が通信失敗時に投げるもの → 通信エラー
 * - それ以外の `Error`: 日本語ならそのまま、英語なら詳細として添える
 */
export function describeApiError(err: unknown): SsmlDictionaryErrorInfo {
  if (err instanceof ApiError) {
    return describeHttpError(err.status, err.message);
  }
  if (err instanceof TypeError) {
    return { message: NETWORK_ERROR_MESSAGE, field: null };
  }
  if (err instanceof Error) {
    return {
      message: containsJapanese(err.message)
        ? err.message
        : `${UNEXPECTED_ERROR_MESSAGE}(詳細: ${err.message})`,
      field: null,
    };
  }
  return { message: UNEXPECTED_ERROR_MESSAGE, field: null };
}

/** 対応済みの警告コード (`index.py` の `_build_ssml_dictionary_warnings` と対) */
export const KNOWN_WARNING_CODES = ['replacement_in_other_source', 'accent_unverified'] as const;

/**
 * 保存時の警告 (保存は成功しているが確認してほしい点) を日本語にする。
 * 未知のコードはサーバのメッセージをそのまま返す。
 */
export function translateWarning(warning: SsmlDictionaryWarning): string {
  if (warning.code === 'replacement_in_other_source') {
    // 例: "replacement is contained in other source_text: 中百舌鳥, 百舌鳥"
    const separator = ': ';
    const index = warning.message.indexOf(separator);
    const sources =
      index >= 0
        ? warning.message
            .slice(index + separator.length)
            .split(', ')
            .filter((s) => s !== '')
        : [];
    const list = sources.map((s) => `「${s}」`).join('');
    return list
      ? `置換後の文字列が、別の変換元${list}に含まれています。意図しない再置換が起きないか確認してください`
      : '置換後の文字列が、別の変換元に含まれています。意図しない再置換が起きないか確認してください';
  }
  if (warning.code === 'accent_unverified') {
    return 'アクセント辞典は、効く語と効かない語があります。試聴して実際の音を確認し、所見を補足欄に記録してください';
  }
  return warning.message;
}

/**
 * 登録 / 更新の完了ポップアップの本文を組み立てる。
 *
 * 警告 (保存は成功したが確認してほしい点) がある場合は、完了の文言の後ろに
 * 「次の点を確認してください。」と箇条書きで続け、1 つのポップアップにまとめる。
 * 警告がなければ完了の文言だけを返す。
 */
export function buildSavedNoticeMessage(
  action: '登録' | '更新',
  sourceText: string,
  warnings: readonly SsmlDictionaryWarning[],
): string {
  const done = `「${sourceText}」を${action}しました`;
  if (warnings.length === 0) return done;
  const items = warnings.map((warning) => `・${translateWarning(warning)}`).join('\n');
  return `${done}\n\n次の点を確認してください。\n${items}`;
}
